import { useParams, Link } from "react-router-dom";
import { categories, articles } from "@/mocks/data";
import { articleImages } from "@/mocks/images";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到 - kaiyun.con 官方导航"
          description="未找到该分类，请浏览其他分类内容。"
          keywords="kaiyun.con,开云平台,分类浏览"
          canonicalUrl="https://kaiyun.con/category"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-12">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-emotion-sad-line text-2xl text-secondary-500"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您访问的分类不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === category.slug);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - kaiyun.con 官方导航`,
    url: `https://kaiyun.con/category/${category.slug}`,
    description: category.description,
    mainEntity: categoryArticles.map((a) => ({
      "@type": "Article",
      headline: a.title,
      url: `https://kaiyun.con/article/${a.slug}`,
      description: a.excerpt,
      datePublished: a.publishedAt,
    })),
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "分类浏览", href: "/category/pingtai-daohang" },
    { label: category.name },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - kaiyun.con 官方导航`}
        description={`${category.description} kaiyun.con 为您提供最全面的${category.name}相关文章与指南。`}
        keywords={`kaiyun.con,${category.name},开云平台,${category.name}指南`}
        canonicalUrl={`https://kaiyun.con/category/${category.slug}`}
        ogType="website"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${category.icon} text-xl text-primary-600`}></i>
                </div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-600 max-w-[640px]">
                {category.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              <Link
                to="/category/pingtai-daohang"
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
              >
                全部分类
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    c.slug === category.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={articleImages[article.imageSeq] || articleImages["art-nav-01"]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-file-list-3-line text-xl text-secondary-400"></i>
                </div>
                <p className="text-sm text-foreground-500">暂无相关文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}